import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Plus, Globe2, Lock, Users2 } from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";
import { StatusBadge } from "./StatusBadge";
import { AdminPagination } from "./AdminPagination";
import { COUNTRY_NAMES_ES } from "./AdminLocationSelector";
import { CURRENCIES, BILLING_TYPES, priceLabel, durationLabel, listingsLabel } from "./planConstants";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPlansPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState("");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const limit = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/plans`, {
        headers: getAdminAuthHeaders(),
        params: {
          keyword: keyword || undefined, status: statusFilter || undefined, billing_type: typeFilter || undefined,
          currency: currencyFilter || undefined, country_code: countryFilter || undefined, scope: scopeFilter || undefined,
          sort_by: sortBy, page, limit,
        },
      });
      setPlans(res.data.plans);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail) || "No se pudieron cargar los planes");
      setPlans([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [keyword, statusFilter, typeFilter, currencyFilter, countryFilter, scopeFilter, sortBy, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [keyword, statusFilter, typeFilter, currencyFilter, countryFilter, scopeFilter]);

  const toggleActive = async (plan) => {
    setActionLoading(plan.plan_id);
    try {
      await axios.post(`${API}/admin/plans/${plan.plan_id}/${plan.status === "active" ? "deactivate" : "activate"}`, {}, { headers: getAdminAuthHeaders() });
      toast.success(plan.status === "active" ? `${plan.name} desactivado` : `${plan.name} activado`);
      await load();
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail) || "No se pudo actualizar el plan");
    } finally {
      setActionLoading("");
    }
  };

  const duplicate = async (plan) => {
    setActionLoading(plan.plan_id);
    try {
      const res = await axios.post(`${API}/admin/plans/${plan.plan_id}/duplicate`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`"${plan.name}" duplicado`);
      navigate(`/admin/plans/${res.data.plan_id}`);
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail) || "No se pudo duplicar el plan");
    } finally {
      setActionLoading("");
    }
  };

  const confirmArchive = async () => {
    setActionLoading(archiveTarget.plan_id);
    try {
      await axios.post(`${API}/admin/plans/${archiveTarget.plan_id}/archive`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${archiveTarget.name} fue archivado`);
      setArchiveTarget(null);
      await load();
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail) || "No se pudo archivar el plan");
    } finally {
      setActionLoading("");
    }
  };

  return (
    <div data-testid="admin-plans-page">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Planes y Accesos</h1>
          <p className="text-sm text-slate-500">Constructor flexible de planes — públicos, privados, trials y acceso manual</p>
        </div>
        <Button data-testid="admin-plans-create-button" onClick={() => navigate("/admin/plans/new")} className="bg-[#028CF8] hover:bg-[#0270C5] shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Crear plan
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="admin-plans-search-input" value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Buscar por plan o empresa..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <select data-testid="admin-plans-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
          <option value="archived">Archivado</option>
        </select>
        <select data-testid="admin-plans-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todo tipo de cobro</option>
          {BILLING_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <select data-testid="admin-plans-currency-filter" value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Toda moneda</option>
          {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
        </select>
        <select data-testid="admin-plans-country-filter" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los países</option>
          {Object.entries(COUNTRY_NAMES_ES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
        <select data-testid="admin-plans-scope-filter" value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Público y privado</option>
          <option value="public">Solo público</option>
          <option value="private">Solo privado</option>
        </select>
        <select data-testid="admin-plans-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="recent">Ordenar: Fecha de creación</option>
          <option value="name">Ordenar: Nombre (A-Z)</option>
          <option value="price">Ordenar: Precio</option>
          <option value="companies">Ordenar: Cantidad de empresas</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm table-fixed" data-testid="admin-plans-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-3 w-[26%]">Plan</th>
              <th className="text-left px-3 py-3 w-[16%]">Precio / Cobro</th>
              <th className="text-left px-3 py-3 w-[16%]">Duración / Avisos</th>
              <th className="text-left px-3 py-3 w-[10%]">Estado</th>
              <th className="text-left px-3 py-3 w-[13%]">Empresas / Creado</th>
              <th className="text-left px-3 py-3 w-[19%]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.plan_id} data-testid={`admin-plan-row-${p.plan_id}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                <td className="px-3 py-2.5">
                  <p className="font-medium text-slate-900 truncate" title={p.name}>{p.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded ${p.scope === "private" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`}>
                      {p.scope === "private" ? <Lock className="h-3 w-3" /> : <Globe2 className="h-3 w-3" />} {p.scope === "private" ? p.company_name : "Público"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{p.countries === "all" ? "Todos los países" : (p.countries || []).map((c) => COUNTRY_NAMES_ES[c] || c).join(", ")}</p>
                </td>
                <td className="px-3 py-2.5 text-slate-700">
                  <p className="font-medium">{priceLabel(p)}</p>
                  <p className="text-xs text-slate-500">{BILLING_TYPES.find((b) => b.value === p.billing_type)?.label}</p>
                </td>
                <td className="px-3 py-2.5 text-slate-600">
                  <p>{durationLabel(p.duration_amount, p.duration_unit)}</p>
                  <p className="text-xs text-slate-500">{listingsLabel(p.listings_mode, p.listings_amount)}</p>
                </td>
                <td className="px-3 py-2.5"><StatusBadge value={p.status} testId={`admin-plan-status-${p.plan_id}`} /></td>
                <td className="px-3 py-2.5 text-slate-600">
                  <p className="flex items-center gap-1"><Users2 className="h-3.5 w-3.5 text-slate-400" /> {p.companies_using_count || 0}</p>
                  <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</p>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-2.5 flex-wrap">
                    <button data-testid={`admin-plan-view-${p.plan_id}`} onClick={() => navigate(`/admin/plans/${p.plan_id}`)} className="text-xs font-semibold text-[#028CF8] hover:underline">Ver</button>
                    {p.status !== "archived" && (
                      <>
                        <button data-testid={`admin-plan-duplicate-${p.plan_id}`} disabled={actionLoading === p.plan_id} onClick={() => duplicate(p)} className="text-xs font-semibold text-slate-500 hover:underline disabled:opacity-40">Duplicar</button>
                        <button data-testid={`admin-plan-toggle-${p.plan_id}`} disabled={actionLoading === p.plan_id} onClick={() => toggleActive(p)} className={`text-xs font-semibold hover:underline disabled:opacity-40 ${p.status === "active" ? "text-amber-600" : "text-emerald-600"}`}>
                          {p.status === "active" ? "Desactivar" : "Activar"}
                        </button>
                        <button data-testid={`admin-plan-archive-${p.plan_id}`} disabled={actionLoading === p.plan_id} onClick={() => setArchiveTarget(p)} className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-40">Archivar</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && plans.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500 text-sm">Sin resultados</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-[#028CF8] animate-spin" /></div>}
      </div>

      <AdminPagination page={page} total={total} limit={limit} onPageChange={setPage} testidPrefix="admin-plans" />

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="admin-plan-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar "{archiveTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                {(archiveTarget?.companies_using_count || 0) > 0 && (
                  <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-sm">
                    {archiveTarget.companies_using_count} empresa(s) están usando este plan actualmente. Sus accesos activos NO se ven afectados, pero el plan ya no podrá asignarse a empresas nuevas.
                  </p>
                )}
                <p className="text-slate-600">Se conserva el plan y todo su historial de asignaciones — no se borra físicamente.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-plan-archive-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-plan-archive-confirm-button" onClick={confirmArchive} className="bg-red-600 hover:bg-red-700">Archivar plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
